import { TAU, type World, type Body, type BodyPart } from "fast-2d";
import type { Camera } from "./Camera";

export type DebugOverlayPainter = (
	world: World,
	ctx: CanvasRenderingContext2D,
	cam: Camera,
) => void;

export interface RenderViewport {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface RenderPane {
	world: World;
	camera: Camera;
	viewport: RenderViewport;
	overlay?: (ctx: CanvasRenderingContext2D, cam: Camera) => void;
	hidden?: ReadonlySet<Body>;
	colorSeed?: "id" | "order";
}

/** The slice of a joint the renderer needs to draw its link (joints expose these). */
interface RenderableConstraint {
	readonly bodyA: Body;
	readonly bodyB: Body | null;
	readonly localAnchorA?: { x: number; y: number };
	readonly localAnchorB?: { x: number; y: number };
	/** Pulley joints expose two fixed world pulley points. */
	readonly groundAnchorA?: { x: number; y: number };
	readonly groundAnchorB?: { x: number; y: number };
	/** Mouse/drag joints expose a single anchor on `bodyA` and a moving target. */
	readonly localAnchor?: { x: number; y: number };
	readonly target?: { x: number; y: number };
}

/** Rotate a body-local anchor into world space. */
function anchorWorld(body: Body, local: { x: number; y: number }): { x: number; y: number } {
	const c = Math.cos(body.angle);
	const s = Math.sin(body.angle);
	return {
		x: body.position.x + local.x * c - local.y * s,
		y: body.position.y + local.x * s + local.y * c,
	};
}

/**
 * A minimal, dependency-free Canvas2D renderer. The engine stays
 * renderer-agnostic — this lives entirely in the website and only reads the
 * public `Body` surface (`worldVerts`, `position`, `radius`, `angle`, …). A
 * WebGPU/WebGL renderer can slot in behind the same interface later.
 */
export class CanvasRenderer {
	private readonly ctx: CanvasRenderingContext2D;
	private dpr = 1;
	private debugOverlay: DebugOverlayPainter | null = null;

	constructor(private readonly canvas: HTMLCanvasElement) {
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("Canvas 2D context is not available");
		this.ctx = ctx;
	}

	/** Resize the backing store to the element's CSS size × devicePixelRatio. */
	resize(camera: Camera): void {
		const rect = this.canvas.getBoundingClientRect();
		this.dpr = Math.min(window.devicePixelRatio || 1, 2);
		this.canvas.width = Math.max(1, Math.round(rect.width * this.dpr));
		this.canvas.height = Math.max(1, Math.round(rect.height * this.dpr));
		camera.setViewport(rect.width, rect.height);
	}

	setStatusText(text: string): void {
		this.canvas.setAttribute("aria-label", text);
		this.canvas.dataset.renderStatus = text;
	}

	setDebugOverlay(painter: DebugOverlayPainter | null): void {
		this.debugOverlay = painter;
	}

	render(
		world: World,
		camera: Camera,
		overlay?: (ctx: CanvasRenderingContext2D, cam: Camera) => void,
		hidden?: ReadonlySet<Body>,
	): void {
		this.setStatusText("Fast2D live physics demo");
		this.renderPanes([
			{
				world,
				camera,
				viewport: { x: 0, y: 0, width: camera.screenW, height: camera.screenH },
				overlay,
				hidden,
			},
		]);
	}

	renderPanes(
		panes: readonly RenderPane[],
		overlay?: (ctx: CanvasRenderingContext2D) => void,
	): void {
		const ctx = this.ctx;
		const screenW = this.canvas.width / this.dpr;
		const screenH = this.canvas.height / this.dpr;
		ctx.save();
		ctx.scale(this.dpr, this.dpr);
		ctx.clearRect(0, 0, screenW, screenH);
		ctx.fillStyle = "#0a0c13";
		ctx.fillRect(0, 0, screenW, screenH);

		for (let i = 0; i < panes.length; i++) {
			const pane = panes[i]!;
			const { x, y, width, height } = pane.viewport;
			pane.camera.setViewport(width, height);

			ctx.save();
			ctx.beginPath();
			ctx.rect(x, y, width, height);
			ctx.clip();
			ctx.translate(x, y);
			ctx.fillStyle = "#0a0c13";
			ctx.fillRect(0, 0, width, height);

			this.drawGrid(pane.camera);
			this.drawConstraints(pane.world, pane.camera);

			const bodies = pane.world.bodies;
			for (let j = 0; j < bodies.length; j++) {
				const body = bodies[j]!;
				if (pane.hidden?.has(body)) continue;
				this.drawBody(body, pane.camera, pane.colorSeed === "order" ? j + 1 : body.id);
			}

			if (this.debugOverlay) this.debugOverlay(pane.world, ctx, pane.camera);
			if (pane.overlay) pane.overlay(ctx, pane.camera);
			ctx.restore();
		}

		if (overlay) overlay(ctx);
		ctx.restore();
	}

	private drawGrid(cam: Camera): void {
		const ctx = this.ctx;
		const x0 = Math.floor(cam.screenToWorldX(0));
		const x1 = Math.ceil(cam.screenToWorldX(cam.screenW));
		const y0 = Math.floor(cam.screenToWorldY(0));
		const y1 = Math.ceil(cam.screenToWorldY(cam.screenH));

		ctx.lineWidth = 1;
		ctx.strokeStyle = "rgba(255,255,255,0.04)";
		ctx.beginPath();
		for (let x = x0; x <= x1; x++) {
			const sx = cam.worldToScreenX(x);
			ctx.moveTo(sx, 0);
			ctx.lineTo(sx, cam.screenH);
		}
		for (let y = y0; y <= y1; y++) {
			const sy = cam.worldToScreenY(y);
			ctx.moveTo(0, sy);
			ctx.lineTo(cam.screenW, sy);
		}
		ctx.stroke();
	}

	/** Draw joint/constraint links as lines between their world anchor points. */
	private drawConstraints(world: World, cam: Camera): void {
		const constraints = world.constraints as ReadonlyArray<RenderableConstraint>;
		if (constraints.length === 0) return;
		const ctx = this.ctx;
		ctx.lineWidth = 2;
		ctx.strokeStyle = "rgba(120, 200, 255, 0.55)";
		for (let i = 0; i < constraints.length; i++) {
			const c = constraints[i]!;
			// Mouse/drag joint: a "grab" line from the held point to the cursor.
			if (c.localAnchor && c.target) {
				const grip = anchorWorld(c.bodyA, c.localAnchor);
				ctx.strokeStyle = "rgba(255, 210, 90, 0.85)";
				ctx.beginPath();
				ctx.moveTo(cam.worldToScreenX(grip.x), cam.worldToScreenY(grip.y));
				ctx.lineTo(cam.worldToScreenX(c.target.x), cam.worldToScreenY(c.target.y));
				ctx.stroke();
				ctx.fillStyle = "rgba(255, 210, 90, 0.95)";
				ctx.beginPath();
				ctx.arc(cam.worldToScreenX(grip.x), cam.worldToScreenY(grip.y), 3, 0, TAU);
				ctx.fill();
				ctx.strokeStyle = "rgba(120, 200, 255, 0.55)";
				continue;
			}
			if (!c.localAnchorA || !c.localAnchorB) continue;
			// Pulley joint: rope runs anchorA → pulleyA → pulleyB → anchorB.
			if (c.bodyB && c.groundAnchorA && c.groundAnchorB) {
				const a = anchorWorld(c.bodyA, c.localAnchorA);
				const b = anchorWorld(c.bodyB, c.localAnchorB);
				const ga = c.groundAnchorA;
				const gb = c.groundAnchorB;
				ctx.beginPath();
				ctx.moveTo(cam.worldToScreenX(a.x), cam.worldToScreenY(a.y));
				ctx.lineTo(cam.worldToScreenX(ga.x), cam.worldToScreenY(ga.y));
				ctx.lineTo(cam.worldToScreenX(gb.x), cam.worldToScreenY(gb.y));
				ctx.lineTo(cam.worldToScreenX(b.x), cam.worldToScreenY(b.y));
				ctx.stroke();
				ctx.fillStyle = "rgba(120, 200, 255, 0.9)";
				for (const p of [a, ga, gb, b]) {
					ctx.beginPath();
					ctx.arc(cam.worldToScreenX(p.x), cam.worldToScreenY(p.y), 2.5, 0, TAU);
					ctx.fill();
				}
				continue;
			}
			const a = anchorWorld(c.bodyA, c.localAnchorA);
			const b = c.bodyB
				? anchorWorld(c.bodyB, c.localAnchorB)
				: { x: c.localAnchorB.x, y: c.localAnchorB.y };
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(a.x), cam.worldToScreenY(a.y));
			ctx.lineTo(cam.worldToScreenX(b.x), cam.worldToScreenY(b.y));
			ctx.stroke();
			// Small dots at the anchor points.
			ctx.fillStyle = "rgba(120, 200, 255, 0.9)";
			for (const p of [a, b]) {
				ctx.beginPath();
				ctx.arc(cam.worldToScreenX(p.x), cam.worldToScreenY(p.y), 2.5, 0, TAU);
				ctx.fill();
			}
		}
	}

	private drawBody(body: Body, cam: Camera, colorKey: number): void {
		// Ensure cached world geometry reflects the latest pose.
		body.synchronize();
		const ctx = this.ctx;

		const sleeping = body.isSleeping;
		const fill = body.isStatic
			? "#2b3145"
			: `hsl(${(colorKey * 47) % 360} 68% ${sleeping ? 38 : 58}%)`;
		const stroke = body.isStatic ? "#3c4461" : "rgba(0,0,0,0.45)";

		ctx.globalAlpha = sleeping ? 0.78 : 1;
		ctx.lineWidth = 1.5;
		ctx.fillStyle = fill;
		ctx.strokeStyle = stroke;

		if (body.isCompound || body.partCount > 1) {
			for (let i = 0; i < body.parts.length; i++) this.drawBodyPart(body.parts[i]!, body, cam);
		} else if (body.isCircle) {
			const cx = cam.worldToScreenX(body.position.x);
			const cy = cam.worldToScreenY(body.position.y);
			const r = body.radius * cam.scale;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, TAU);
			ctx.fill();
			ctx.stroke();
			// Orientation spoke so rolling is visible.
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(cx + Math.cos(body.angle) * r, cy + Math.sin(body.angle) * r);
			ctx.strokeStyle = "rgba(0,0,0,0.35)";
			ctx.stroke();
		} else {
			const v = body.worldVerts;
			ctx.beginPath();
			ctx.moveTo(cam.worldToScreenX(v[0]!), cam.worldToScreenY(v[1]!));
			for (let i = 2; i < v.length; i += 2) {
				ctx.lineTo(cam.worldToScreenX(v[i]!), cam.worldToScreenY(v[i + 1]!));
			}
			ctx.closePath();
			ctx.fill();
			ctx.stroke();
		}

		ctx.globalAlpha = 1;
	}

	private drawBodyPart(part: BodyPart, body: Body, cam: Camera): void {
		const ctx = this.ctx;
		if (part.isCircle) {
			ctx.save();
			const cx = cam.worldToScreenX(part.worldCenterX);
			const cy = cam.worldToScreenY(part.worldCenterY);
			const r = part.radius * cam.scale;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, TAU);
			ctx.fill();
			ctx.stroke();
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(cx + Math.cos(body.angle) * r, cy + Math.sin(body.angle) * r);
			ctx.strokeStyle = "rgba(0,0,0,0.35)";
			ctx.stroke();
			ctx.restore();
			return;
		}

		const v = part.worldVerts;
		ctx.beginPath();
		ctx.moveTo(cam.worldToScreenX(v[0]!), cam.worldToScreenY(v[1]!));
		for (let i = 1; i < part.vertexCount; i++) {
			const offset = i * 2;
			ctx.lineTo(cam.worldToScreenX(v[offset]!), cam.worldToScreenY(v[offset + 1]!));
		}
		ctx.closePath();
		ctx.fill();
		ctx.stroke();
	}
}
